import { readConfigHomeHomeHistory } from '@racepointenergy/swc/scs/read-config-home-home-history'
import type { HistoricalConfigDeployment } from '@racepointenergy/swc/scs/read-config-home-home-history'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'

import { useAppSelector } from 'app/hooks'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import { commonMessages } from 'app/i18n/messages'
import { CommonLinkButton } from 'components/Button'
import { ConfigFile } from 'components/Icons'
import { KebabMenu } from 'components/KebabMenu'
import { DownloadButtonCell } from 'components/Table'
import { currentUserSelector } from 'features/auth/login/selectors'
import { selectSoftwareUpdatesInProgress } from 'features/home/components/HomeSoftware/HomeCloudSoftwareHistory/selectors'
import { useHomeRouteParams } from 'features/home/hooks'
import { selectedHomeSelector } from 'features/home/selectors'

//import { hasSystemAccessEnabled } from 'features/home/utils'
import {
  HomeConfigActionCellProgressBar,
  HomeConfigFilesMobileDeployingContainer,
  HomeConfigFilesMobileProgressContainer,
} from '../../HomeConfigFiles/styles'
import { homeConfigurationMessages } from '../../messages'
import { deployDomainConfigThunk } from '../../slice'
import {
  ConfigKebabContainer,
  ConfigMobileCardContainer,
  ConfigMobileCardLabelWrapper,
  ConfigMobileCardTitle,
  HomeConfigurationWhiteBlock,
} from '../../styles'
import DeploymentStatus from '../components/DeploymentStatus'

interface MobileHomeDeploymentsHistoryProps {
  original: HistoricalConfigDeployment
  values: HistoricalConfigDeployment | Record<string, any>
}

export function MobileHomeDeploymentsHistory(
  props: MobileHomeDeploymentsHistoryProps
) {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const [refetchHomesHomeHistory] = readConfigHomeHomeHistory.useLazyQuery()
  const home = useAppSelector(selectedHomeSelector.data)
  const user = useAppSelector(currentUserSelector)
  const softwareUpdatesInProgress = useAppSelector(
    selectSoftwareUpdatesInProgress
  )

  const isUpdateInProgress = softwareUpdatesInProgress?.length > 0
  const { homeId } = useHomeRouteParams()

  const [progress, setProgress] = useState(0)

  const hasSystemAccess = false //hasSystemAccessEnabled(home)

  const { values, original } = props
  const fileName = `${values.name}.tar.gz`

  const handleDeploy = async () => {
    if (!(home?.id && user?.id && original?.id)) {
      console.warn(`Not all the request parameters are exists`)
      return
    }

    try {
      await dispatch(
        deployDomainConfigThunk(
          {
            config_id: original.id,
            home_id: home.id,
            integrator_id: user.id,
          },
          setProgress
        )
      )
    } catch (e) {
      throw e
    } finally {
      setProgress(0)
      refetchHomesHomeHistory({ homeId })
    }
  }

  return (
    <HomeConfigurationWhiteBlock progress={progress}>
      <ConfigMobileCardContainer>
        <ConfigMobileCardTitle>
          <ConfigFile />
          <span>{values.name}</span>
        </ConfigMobileCardTitle>
        {!progress && (
          <KebabMenu>
            <ConfigKebabContainer>
              {hasSystemAccess && (
                <CommonLinkButton
                  onClick={handleDeploy}
                  disabled={isUpdateInProgress}
                >
                  {formatMessage(homeConfigurationMessages.redeploy)}
                </CommonLinkButton>
              )}
              <DownloadButtonCell url={original.configUrl} fileName={fileName}>
                {formatMessage(commonMessages.download)}
              </DownloadButtonCell>
            </ConfigKebabContainer>
          </KebabMenu>
        )}
      </ConfigMobileCardContainer>
      {progress ? (
        <HomeConfigFilesMobileDeployingContainer>
          <span>{formatMessage(homeConfigurationMessages.deploying)}</span>
          <HomeConfigFilesMobileProgressContainer>
            <HomeConfigActionCellProgressBar progress={progress} />
          </HomeConfigFilesMobileProgressContainer>
        </HomeConfigFilesMobileDeployingContainer>
      ) : (
        <>
          <ConfigMobileCardLabelWrapper>
            <span>{formatMessage(homeConfigurationMessages.dateDeployed)}</span>
            <IntlFormattedDate date={values.date} withTime />
          </ConfigMobileCardLabelWrapper>
          <ConfigMobileCardLabelWrapper>
            <DeploymentStatus {...original} />
          </ConfigMobileCardLabelWrapper>
        </>
      )}
    </HomeConfigurationWhiteBlock>
  )
}
